/**
 * ============================================================================
 * DYNAMIC PDP ACCORDIONS
 * ----------------------------------------------------------------------------
 * Loaded only on the product page by snippets/custom-dynamic-product-accordions.liquid.
 * ============================================================================
 */
(function () {
  "use strict";

  const DynamicProductAccordions = {
    selectors: {
      root: "[data-kess-dynamic-product-accordions]",
      panel: "[data-kess-dynamic-accordion-panel]",
      panelTemplate: "[data-kess-dynamic-accordion-template]",
      ingredient: ".variant-ingredient-content",
      shadeTab: ".kess-shade-tab-content",
      howToSlider: "[data-kess-dynamic-how-to-slider]",
    },

    init(container = document) {
      const roots = container.querySelectorAll(this.selectors.root);
      if (!roots.length) return;

      roots.forEach((root) => {
        if (root.dataset.kessDynamicAccordionsInitialized === "true") return;
        root.dataset.kessDynamicAccordionsInitialized = "true";
        this.showVariant(root, this.getInitialVariantId(root));
        this.initSliders(root);
      });

      // Configurators and sets apply their (pre)selections asynchronously after this
      // runs, so re-sync ingredient visibility once they've settled.
      const resync = () => {
        document.querySelectorAll(this.selectors.root).forEach((root) => {
          const activePanel = root.querySelector(`${this.selectors.panel}.is-active`);
          this.syncIngredients(root, activePanel ? activePanel.dataset.variantId || "" : "");
        });
      };
      requestAnimationFrame(resync);
      setTimeout(resync, 600);

      if (this.boundVariantChange) return;
      this.boundVariantChange = true;
      document.addEventListener("variant:changed", (event) => {
        const variantId = event.detail?.variant?.id;
        if (!variantId) return;

        document.querySelectorAll(this.selectors.root).forEach((root) => {
          const mainVariantIds = (root.dataset.mainVariantIds || "").split(",");
          if (root.dataset.refillConfigurator !== "true" && !mainVariantIds.includes(String(variantId))) return;
          this.showVariant(root, variantId);
        });
      });

      // React to each configurator shade pick so shade-specific ingredients update
      // immediately (and a dual configurator can show both selections).
      document.addEventListener("click", (event) => {
        const shade = event.target.closest("[data-kess-duo-shade], [data-kess-set-shade]");
        if (!shade) return;
        requestAnimationFrame(() => {
          const scope = shade.closest(".shopify-section") || document;
          scope.querySelectorAll(this.selectors.root).forEach((root) => {
            const activePanel = root.querySelector(`${this.selectors.panel}.is-active`);
            this.syncIngredients(root, activePanel ? activePanel.dataset.variantId || "" : "");
          });
        });
      });
    },

    getInitialVariantId(root) {
      const activePanel = root.querySelector(`${this.selectors.panel}.is-active`);
      return activePanel?.dataset.variantId;
    },

    showVariant(root, variantId) {
      const variantIdString = String(variantId || "");

      // Configurator products render a single always-active
      // panel — the combo variant id won't match it, so don't switch panels;
      // shade selection alone drives which ingredients show.
      if (root.dataset.refillConfigurator === "true") {
        this.syncIngredients(root, variantIdString);
        return;
      }

      this.materializeVariantPanel(root, variantIdString);

      // Carry the open/closed state of each row across the panel swap so a row
      // the shopper opened stays open (and a closed one stays closed) when the
      // variant changes.
      const currentPanel = root.querySelector(`${this.selectors.panel}.is-active`);
      const openKeys = currentPanel ? this.getOpenRowKeys(currentPanel) : null;

      root.querySelectorAll(this.selectors.panel).forEach((panel) => {
        const isActive = panel.dataset.variantId === variantIdString;
        panel.hidden = !isActive;
        panel.classList.toggle("is-active", isActive);
        if (isActive) {
          if (openKeys) this.applyOpenRowKeys(panel, openKeys);
          this.initSliders(panel);
        }
      });

      this.syncIngredients(root, variantIdString);
    },

    materializeVariantPanel(root, variantIdString) {
      const template = Array.from(root.querySelectorAll(this.selectors.panelTemplate)).find(
        (candidate) => candidate.dataset.variantId === variantIdString,
      );
      const panel = template?.content.firstElementChild;
      if (!template || !panel) return;

      template.replaceWith(panel);
    },

    // Decide which ingredient blocks are visible:
    //  - Configurator refills (a shade button targets one of their
    //    variants): the WHOLE refill product is "reactive" — every one of its
    //    ingredient blocks stays hidden until the shopper picks the matching
    //    shade, then that shade appears (a duo shows both picked shades). This
    //    also hides refill variants that aren't offered as shades. Works for a
    //    configurator on its own page and as a component inside a set.
    //  - The main product's own variants (simple multi-variant product): show
    //    only the selected variant.
    //  - Everything else (set component representatives):
    //    keep the server-rendered visibility.
    syncIngredients(root, variantIdString) {
      // Universe of refill shades offered within this product section — standalone
      // configurators use [data-kess-duo-shade]; set components use
      // [data-kess-set-shade]. Used to mark which refill products are "reactive".
      const shadeVariantIds = new Set();
      const scope = root.closest(".shopify-section") || document;
      scope.querySelectorAll("[data-kess-duo-shade], [data-kess-set-shade]").forEach((button) => {
        if (button.dataset.shadeId) shadeVariantIds.add(button.dataset.shadeId);
      });

      // Currently selected refill shades, gathered from every selection source —
      // the shade buttons' .is-selected only reflects the slot being viewed, so
      // the authoritative per-slot picks come from the configurators/components:
      //  - standalone configurators publish all slots on the configurator
      //  - set components keep each slot's pick in their controller state
      //  - .is-selected is a final fallback
      const selectedShadeIds = new Set();
      scope.querySelectorAll("[data-kess-duo-configurator]").forEach((configurator) => {
        (configurator.dataset.kessSelectedRefillIds || "").split(",").forEach((id) => {
          const trimmed = id.trim();
          if (trimmed) selectedShadeIds.add(trimmed);
        });
      });
      scope.querySelectorAll("[data-kess-set-component]").forEach((component) => {
        const state = component.__kessSet;
        if (!state) return;
        [state.shade1, state.shade2].forEach((shade) => {
          if (shade && shade.id) selectedShadeIds.add(String(shade.id));
        });
      });
      scope
        .querySelectorAll("[data-kess-duo-shade].is-selected, [data-kess-set-shade].is-selected")
        .forEach((button) => {
          if (button.dataset.shadeId) selectedShadeIds.add(button.dataset.shadeId);
        });

      const mainVariantIds = (root.dataset.mainVariantIds || "").split(",").filter(Boolean);

      // Structured ingredients react to the selected shade(s).
      const ingredientsInfo = this.applyShadeVisibility(
        root,
        this.selectors.ingredient,
        variantIdString,
        selectedShadeIds,
        shadeVariantIds,
        mainVariantIds,
      );

      // Variant-level tab content (e.g. a refill's per-shade description) reacts
      // to shade selection the same way — so a configurator shows the
      // selected shade's description, not a frozen first-variant one. Dedup is
      // scoped to each tab (accordion row) so the same shade can appear in more
      // than one tab (e.g. an "application" tab and a "more info" tab).
      root.querySelectorAll(".accordion-details").forEach((row) => {
        if (!row.querySelector(this.selectors.shadeTab)) return;
        this.applyShadeVisibility(
          row,
          this.selectors.shadeTab,
          variantIdString,
          selectedShadeIds,
          shadeVariantIds,
          mainVariantIds,
        );
      });

      // Default state for the ingredients dropdown: when the ingredients are
      // shade-driven and nothing is shown yet, reveal the placeholder text.
      this.toggleIngredientsEmptyState(root, ingredientsInfo.hasReactive);
    },

    // Toggles a group of shade-keyed blocks (ingredients or variant tab content):
    //  - Blocks of a "reactive" refill product show only when their shade is
    //    selected (deduped so a shade shared across blocks shows once).
    //  - The main product's own variants show only the selected variant.
    //  - Everything else keeps its server-rendered visibility.
    // Returns { hasReactive } so callers can drive empty states.
    applyShadeVisibility(root, selector, variantIdString, selectedShadeIds, shadeVariantIds, mainVariantIds) {
      const blocks = Array.from(root.querySelectorAll(selector));
      const idOf = (el) => {
        const cls = Array.from(el.classList).find((c) => c.startsWith("ingredients-"));
        return cls ? cls.slice("ingredients-".length) : "";
      };

      // A refill product is reactive if any of its variants is offered as a shade.
      const reactiveProductIds = new Set();
      blocks.forEach((el) => {
        if (shadeVariantIds.has(idOf(el)) && el.dataset.ingredientProductId) {
          reactiveProductIds.add(el.dataset.ingredientProductId);
        }
      });

      // Each selected shade is shown once — if the same refill (shade) is rendered
      // more than once (e.g. a set whose duo and mono share a refill), don't double it.
      const shownShadeIds = new Set();
      blocks.forEach((el) => {
        const blockId = idOf(el);
        if (reactiveProductIds.has(el.dataset.ingredientProductId)) {
          const show = selectedShadeIds.has(blockId) && !shownShadeIds.has(blockId);
          el.classList.toggle("hide-tabs", !show);
          if (show) shownShadeIds.add(blockId);
        } else if (mainVariantIds.includes(blockId)) {
          el.classList.toggle("hide-tabs", blockId !== variantIdString);
        }
        // else: leave the server-rendered visibility untouched.
      });

      return { hasReactive: reactiveProductIds.size > 0 };
    },

    toggleIngredientsEmptyState(root, hasReactive) {
      const emptyEls = root.querySelectorAll("[data-kess-ingredients-empty]");
      if (!emptyEls.length) return;
      const anyIngredientVisible = Array.from(root.querySelectorAll(".ProductIngredients")).some(
        (el) => !el.classList.contains("hide-tabs"),
      );
      // Only surface the placeholder for shade-driven ingredients with nothing
      // shown — never for static (single/simple) products.
      const show = hasReactive && !anyIngredientVisible;
      emptyEls.forEach((el) => el.classList.toggle("hide-tabs", !show));
    },

    getOpenRowKeys(panel) {
      const keys = new Set();
      panel.querySelectorAll("details[data-accordion-key]").forEach((details) => {
        if (details.open) keys.add(details.dataset.accordionKey);
      });
      return keys;
    },

    applyOpenRowKeys(panel, openKeys) {
      panel.querySelectorAll("details[data-accordion-key]").forEach((details) => {
        const shouldOpen = openKeys.has(details.dataset.accordionKey);
        if (details.open === shouldOpen) return;
        // Set the state instantly (no open/close animation) while keeping the
        // theme's accordion element and its +/- icon (driven by aria-expanded)
        // in sync, so a later click still toggles correctly.
        details.isOpen = shouldOpen;
        details.toggleAttribute("open", shouldOpen);
        details.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
      });
    },

    initSliders(scope) {
      scope.querySelectorAll(this.selectors.howToSlider).forEach((slider) => {
        if (slider.dataset.kessSliderReady === "true") return;

        const wrapper = slider.querySelector(".kess-dynamic-how-to-slider__track");
        const slides = wrapper?.querySelectorAll(".kess-dynamic-how-to-slider__slide") || [];
        if (!wrapper || slides.length < 2 || !window.FoxTheme?.Carousel) return;

        slider.classList.add("swiper");
        wrapper.classList.add("swiper-wrapper");

        const carousel = new window.FoxTheme.Carousel(slider, {
          // Fixed-size cards (260px wide via CSS) with peek — width comes from
          // each slide's CSS, not a fraction of the container.
          slidesPerView: "auto",
          spaceBetween: 16,
          loop: false,
          rewind: false,
          pagination: false,
          navigation: false,
          threshold: 2,
          grabCursor: true,
          simulateTouch: true,
        });

        carousel.init();
        slider.__kessHowToSlider = carousel;
        slider.dataset.kessSliderReady = "true";
      });
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => DynamicProductAccordions.init());
  } else {
    DynamicProductAccordions.init();
  }

  // Re-initialize when the block is re-rendered in the theme editor.
  document.addEventListener("shopify:section:load", (event) => {
    DynamicProductAccordions.init(event.target);
  });
})();
